import { useCallback, useState } from 'react';
import { useResourceDispatch, useResourceState } from '../context/ResourceContext';
import { useUI } from '../context/UIContext';
import { useSystemStatus } from '../context/SystemStatusContext';
import { getContentTypeConfig } from '../components/content/ContentTypeRegistry';
import { fetchResource as genericFetchResource } from '../services/resourceApi';

/**
 * Custom hook to intelligently open resources.
 * It handles hydration (fetching full data) if the resource is partial or missing
 * before opening the view.
 */
export const useResourceOpener = () => {
    const { upsertResource } = useResourceDispatch();
    const { getResourceById } = useResourceState();
    const { openResourceTab, openResourcePreview } = useUI();
    const { addMessage, removeMessage } = useSystemStatus();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Opens a resource, ensuring it is fully hydrated first.
     * 
     * @param {Object|string} identifier - Either a resource object (partial/full) or a resource ID string.
     * @param {string} [type] - Required if identifier is an ID string.
     * @param {Object} [options] - Options for opening.
     * @param {string} [options.mode='tab'] - 'tab' or 'modal'.
     * @param {boolean} [options.forceRefresh=false] - Force a re-fetch even if data exists.
     */
    const openResource = useCallback(async (identifier, type, options = {}) => {
        const { mode = 'tab', forceRefresh = false } = options;
        
        let resourceId;
        let resourceType = type;
        let currentResource = null;

        // 1. Resolve ID and Type
        if (typeof identifier === 'string') {
            resourceId = identifier;
            currentResource = getResourceById(resourceId);
            if (currentResource && !resourceType) {
                resourceType = currentResource.type || currentResource.resource_type;
            }
        } else if (typeof identifier === 'object' && identifier !== null) {
            resourceId = identifier.id;
            resourceType = identifier.type || identifier.resource_type || type;
            currentResource = getResourceById(resourceId) || identifier;
        }

        if (!resourceId) {
            console.error("[useResourceOpener] Cannot open resource: Missing ID");
            return;
        }

        // 2. Check if hydration is needed
        const isPartial = currentResource?._isPartial === true;
        const isMissing = !currentResource;
        const shouldFetch = isMissing || isPartial || forceRefresh;

        if (shouldFetch) {
            const messageId = `opening-${resourceId}`;
            setIsLoading(true);
            setError(null);
            
            // Show loading toast if it takes more than 300ms
            const loadingTimer = setTimeout(() => {
                addMessage({ id: messageId, text: "Loading resource...", severity: 'info', isLoading: true });
            }, 300);

            try {
                // Determine fetcher
                const config = getContentTypeConfig(resourceType);
                let fetchedData = null;

                if (config && config.fetcher) {
                    console.log(`[useResourceOpener] Hydrating ${resourceType} ${resourceId} using registered fetcher.`);
                    fetchedData = await config.fetcher(resourceId);
                } else if (resourceType) {
                    console.log(`[useResourceOpener] Hydrating ${resourceType} ${resourceId} using generic fetcher.`);
                    fetchedData = await genericFetchResource(resourceType, resourceId);
                } else {
                    throw new Error("Unknown resource type, cannot fetch.");
                }

                if (fetchedData) {
                    // Ensure type consistency
                    const finalResource = {
                        ...currentResource, // Keep existing props (like isBookmarked)
                        ...fetchedData,
                        type: resourceType,
                        resource_type: resourceType,
                        _isPartial: false 
                    };
                    
                    // Update global store
                    upsertResource(finalResource);
                    currentResource = finalResource;
                } else {
                    throw new Error("Resource not found.");
                }

            } catch (err) {
                console.error(`[useResourceOpener] Failed to open ${resourceId}:`, err);
                setError(err.message);
                addMessage({ id: `err-${messageId}`, text: `Failed to open: ${err.message}`, severity: 'error' });
                return; // Stop here if failed
            } finally {
                clearTimeout(loadingTimer);
                removeMessage(messageId);
                setIsLoading(false);
            }
        }

        // 3. Open the View
        if (currentResource) {
            if (mode === 'modal') {
                openResourcePreview(currentResource);
            } else {
                openResourceTab(currentResource);
            }
        }

    }, [getResourceById, upsertResource, openResourceTab, openResourcePreview, addMessage, removeMessage]);

    /**
     * Legacy support wrapper
     */
    const refreshAndOpenResource = useCallback(async (resourceId) => {
         const res = getResourceById(resourceId);
         const type = res?.type || res?.resource_type;
         return openResource(resourceId, type, { forceRefresh: true });
    }, [getResourceById, openResource]);

    return { openResource, refreshAndOpenResource, isLoading, error };
};